import { Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Link as VisualLink,
} from '@mui/material';

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { LoadingButton } from '@mui/lab';

import { SALE_ORDER_STATUS } from '@bandido/shared/constants';
import { SaleOrderResponseDto } from '@bandido/shared/dtos';

import { Link } from '@frontend/features/common/components/Link';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { Table } from '@frontend/features/common/components/Table';
import { routes } from '@frontend/router/routes';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { formatISOStringToFriendlyDate } from '@frontend/features/common/helper';
import { saleOrderChipColor } from '@frontend/router/saleorder/list';
import { QueryKeys } from '@frontend/features/common/types';

import { useLinkSaleOrderToCase, useUnlinkSaleOrderFromCase, useGetSaleOrdersForDashboard, useAddSaleOrderNoteToCase, useRemoveSaleOrderNoteFromCase, useGetCaseSaleOrders } from '../../queries';

interface CaseSaleOrdersProps {
  caseId: number;
}

export const LinkToCaseTool: React.FC<CaseSaleOrdersProps> = ({ caseId }) => {

  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useDebouncedState('', 300);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const saleOrderToRemove = useRef<SaleOrderResponseDto | null>(null);

  const { data: relatedSaleOrders } = useGetCaseSaleOrders({
    caseId,
    onErrorCallback: () => {
      addToast({
        message: t('saleOrder.link.notifications.loadError'),
        color: 'error',
      });
    },
  });
  
  const { data: saleOrders, isLoading: saleOrdersLoading} = useGetSaleOrdersForDashboard({
    search: searchTerm,
    pageSize: 10,
    currentPage: 1,
    excludeLinked: true,
    onErrorCallback: () => {
      addToast({
        message: t('saleOrder.link.notifications.loadError'),
        color: 'error'
      });
    },
  });

  const { mutate: addNote, isLoading: isAddingLinkNote } = useAddSaleOrderNoteToCase();
  const { mutate: removeNote, isLoading: isRemovingLinkNote } = useRemoveSaleOrderNoteFromCase();

  const { mutate: link, isLoading: isLinking } = useLinkSaleOrderToCase();
  const { mutate: unlink, isLoading: isUnlinking } = useUnlinkSaleOrderFromCase();

  const unlinkSaleOrder= (saleOrderId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      unlink({
        caseId,
        saleOrderId,
        onSuccess: () => resolve(),
        onError: () => reject(new Error('Unlink failed')),
      });
    });
  };

  const linkSaleOrder = (idToLink: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      link({
        caseId,
        saleOrderId: idToLink,
        onSuccess: () => resolve(),
        onError: () => reject(new Error('Link failed')),
      });
    });
  };

  const addSaleOrderNote = (saleOrderRecord: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      addNote({
        caseId,
        saleOrderId: saleOrderRecord,
        onSuccess: () => resolve(),
        onError: () => reject(new Error('Add note failed')),
      });
    });
  };

  const removeSaleOrderNote = (saleOrderRecord: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      removeNote({
        caseId,
        saleOrderId: saleOrderRecord,
        onSuccess: () => resolve(),
        onError: () => reject(new Error('Remove note failed')),
      });
    });
  };

  const handleUnlink = async (saleOrderId: string, saleOrderRecord: string) => {
    if (!caseId) return;
    try {
      await unlinkSaleOrder(saleOrderId);
      await removeSaleOrderNote(saleOrderRecord);
      
      addToast({
        message: t('saleOrder.link.notifications.unlinkSuccess'),
        color: 'success'
      });
      setUnlinkModalOpen(false);
      queryClient.invalidateQueries([QueryKeys.getCaseSaleOrders]);
      queryClient.invalidateQueries([QueryKeys.getSaleOrdersForDashboard]);
    } catch (error) {
      addToast({
        message: t('saleOrder.link.notifications.unlinkError'),
        color: 'error'
      });
    }
  };

  const handleCloseLinkModal = () => {
    setLinkModalOpen(false);
    setSearchTerm('');
  };

  const handleLink = async (idToLink: string, recordToLink: string) => {
    try {
      await linkSaleOrder(idToLink);
      await addSaleOrderNote(recordToLink);
      
      addToast({
        message: t('saleOrder.link.notifications.linkSuccess'),
        color: 'success'
      });
      queryClient.invalidateQueries([QueryKeys.getCaseSaleOrders]);
      queryClient.invalidateQueries([QueryKeys.getSaleOrdersForDashboard]);
      handleCloseLinkModal();
    } catch (error) {
      addToast({
        message: t('saleOrder.link.notifications.linkError'),
        color: 'error'
      });
    }
  };

  return (
    <>
      {relatedSaleOrders && relatedSaleOrders.length > 0 && (
        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          {relatedSaleOrders?.map((saleOrder) => (
            <Box key={saleOrder.id} maxWidth={365} overflow="hidden">
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Link
                  underline="none"
                  target="_blank"
                  to={routes.dashboard.saleOrder.detail.replace(':id', String(saleOrder.id))}
                >
                  <Typography variant="body2" color="text.primary">
                    SO # {saleOrder.record }
                  </Typography>
                </Link>

                <Box display="flex" gap={1} alignItems="center">
                  <Chip color={saleOrderChipColor[saleOrder.status as SALE_ORDER_STATUS] || 'default'} label={saleOrder.status} />
                  <IconButton
                    size="small"
                    onClick={() => {
                      saleOrderToRemove.current = saleOrder;
                      setUnlinkModalOpen(true);
                    }}
                  >
                    <Delete/>
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
     
      <VisualLink
        onClick={() => setLinkModalOpen(true)}
        underline="none"
        sx={{
          cursor: 'pointer',
          mb: 4,
        }}
      >
        {t('saleOrder.link.action.add')}
      </VisualLink>

      <Dialog
        open={linkModalOpen}
        onClose={handleCloseLinkModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('saleOrder.link.modal.title')}
        </DialogTitle>
        <DialogContent>
          <>
            <Box display="flex" justifyContent="flex-end" my={2} mx={1}>
              <SearchInput placeholder={t('common.search')} onSearch={setSearchTerm} />
            </Box>
            {!saleOrdersLoading && (
              <Table
                height={400}
                columns={[
                  {
                    id: 'id',
                    label: t('saleOrder.list.table.id'),
                    align: 'left',
                    format: (_, saleOrder) => (
                      <Stack>
                        <Link to={routes.dashboard.saleOrder.detail.replace(':id', String(saleOrder.id))}>{saleOrder.record}</Link>
                      </Stack>
                    )
                  },
                  {
                    id: 'amount',
                    label: t('saleOrder.list.table.amount.title'),
                    align: 'left',
                    format: (value) => value ? <>{t('saleOrder.list.table.amount.value', { value })}</> : <>{t('common.tbd')}</>
                  },
                  {
                    id: 'status',
                    label: t('saleOrder.list.table.status'),
                    align: 'left',
                    format: (_, saleOrder) => (<Chip label={t(`saleOrder.status.${saleOrder.status}`)} color={saleOrderChipColor[saleOrder.status as SALE_ORDER_STATUS] || 'default'} />)
                  },
                  {
                    id: 'createdByName',
                    label: t('saleOrder.list.table.createdBy'),
                    align: 'left',
                    format: (_, saleOrder) => (
                      <Stack>
                        <Link to={routes.dashboard.userView.replace(':id', String(saleOrder.authorUserId))}>{saleOrder.authorName} {saleOrder.authorSurname}
                        </Link>
                      </Stack>
                    )
                  },
                  {
                    id: 'createdAt',
                    label: t('saleOrder.list.table.createdAt'),
                    align: 'left',
                    format: (data) => <>{formatISOStringToFriendlyDate(data)}</>
                  },
                  {
                    id: 'caseId',
                    label: t('saleOrder.list.table.case'),
                    align: 'left',
                    minWidth: 90,
                    format: (_, item) => (
                      <LoadingButton
                        loading={isLinking || isAddingLinkNote}
                        size='small'
                        onClick={() => handleLink(item.id, item.record)}
                      >
                        { t('saleOrder.link.modal.action') }
                      </LoadingButton>
                    ),
                  },
                ]}
                isLoading={saleOrdersLoading}
                data={{
                  key: 'id',
                  items: saleOrders?.items || [],
                  totalCount: saleOrders?.count || 0,
                }}
              />
            )}
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkModal}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unlinkModalOpen}
        onClose={() => setUnlinkModalOpen(false)}
      >
        <DialogTitle>
          {t('saleOrder.unlink.modal.title')}
        </DialogTitle>
        <DialogContent>

        <Box display="flex" gap={2}>
          <Typography>
            {t('saleOrder.unlink.modal.message')}
          </Typography>
        </Box>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isUnlinking || isRemovingLinkNote}
            onClick={() => setUnlinkModalOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <LoadingButton
            loading={isUnlinking}
            onClick={() => handleUnlink(
              saleOrderToRemove.current?.id || '',
              saleOrderToRemove.current?.record || ''
            )}
            color="primary"
            variant="contained"
          >
            {t('common.confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
